import { pgTable, text, timestamp, integer, index, foreignKey, jsonb, check, uuid, uniqueIndex } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { bonusType, userBonusStatus, userBonusActionType } from './enums';
import { user } from './user';
import { bonusTemplate } from './pricing-tier';
import { accessGrant, order } from './commerce';
import { adminUser } from './admin';

export const userBonus = pgTable('UserBonus', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  bonusTemplateId: integer(), // null для ручных бонусов
  accessGrantId: integer(), // null для ручных бонусов
  orderId: integer(), // null для ручных бонусов

  // Snapshot метаданных
  name: text().notNull(),
  type: bonusType().notNull(),
  description: text(),

  currentValue: jsonb().notNull(),
  initialValue: jsonb().notNull(),

  // Структурированная мета для quota-бонусов: { targetType, filterCourseType?, subscriptionId? }.
  // Используется checkAndUseQuantityBonus для фильтрации и revokeBySubscription для ревока.
  metadata: jsonb().$type<{
    targetType?: 'COURSE' | 'CHANNEL' | 'BOOK';
    filterCourseType?: 'ONLINE' | 'MEETING' | 'OFFLINE';
    subscriptionId?: number;
  }>(),

  startsAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  expiresAt: timestamp({ precision: 3, mode: 'date' }),
  status: userBonusStatus().default('ACTIVE').notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  // CHECK: валидация типов JSONB
  check('user_bonus_value_type_check', sql`
    (${table.type} = 'NUMBER'  AND jsonb_typeof(${table.currentValue}) = 'number'  AND (${table.currentValue})::numeric >= 0) OR
    (${table.type} = 'BOOLEAN' AND jsonb_typeof(${table.currentValue}) = 'boolean') OR
    (${table.type} = 'ARRAY'   AND jsonb_typeof(${table.currentValue}) = 'array') OR
    (${table.type} = 'STRING'  AND jsonb_typeof(${table.currentValue}) = 'string')
  `),
  check('user_bonus_initial_value_type_check', sql`
    (${table.type} = 'NUMBER'  AND jsonb_typeof(${table.initialValue}) = 'number') OR
    (${table.type} = 'BOOLEAN' AND jsonb_typeof(${table.initialValue}) = 'boolean') OR
    (${table.type} = 'ARRAY'   AND jsonb_typeof(${table.initialValue}) = 'array') OR
    (${table.type} = 'STRING'  AND jsonb_typeof(${table.initialValue}) = 'string')
  `),
  index('UserBonus_userId_status_idx').using('btree', table.userId.asc().nullsLast(), table.status.asc().nullsLast()),
  index('UserBonus_accessGrantId_idx').using('btree', table.accessGrantId.asc().nullsLast()),
  index('UserBonus_bonusTemplateId_idx').using('btree', table.bonusTemplateId.asc().nullsLast()),
  // Partial index for reconciliation
  index('UserBonus_expiration_idx')
    .using('btree', table.expiresAt.asc().nullsLast())
    .where(sql`${table.status} = 'ACTIVE' AND ${table.expiresAt} IS NOT NULL`),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserBonus_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.bonusTemplateId],
    foreignColumns: [bonusTemplate.id],
    name: 'UserBonus_bonusTemplateId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.accessGrantId],
    foreignColumns: [accessGrant.id],
    name: 'UserBonus_accessGrantId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'UserBonus_orderId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

export const userBonusAction = pgTable('UserBonusAction', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userBonusId: integer().notNull(),
  action: userBonusActionType().notNull(),

  previousValue: jsonb().notNull(),
  newValue: jsonb().notNull(),

  adminUserId: integer(), // null для системных действий
  reason: text(),
  idempotencyKey: uuid(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('UserBonusAction_userBonusId_createdAt_idx')
    .using('btree', table.userBonusId.asc().nullsLast(), table.createdAt.asc().nullsLast()),
  index('UserBonusAction_adminUserId_idx')
    .using('btree', table.adminUserId.asc().nullsLast()),
  uniqueIndex('UserBonusAction_idempotencyKey_unique')
    .using('btree', table.idempotencyKey.asc().nullsLast())
    .where(sql`${table.idempotencyKey} IS NOT NULL`),
  foreignKey({
    columns: [table.userBonusId],
    foreignColumns: [userBonus.id],
    name: 'UserBonusAction_userBonusId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'UserBonusAction_adminUserId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);
